/******/ (() => { // webpackBootstrap
/******/ 	"use strict";

;// ./src/js/CartDrawerUpsell.js
class CartDrawerUpsell {
  constructor() {
    this.drawerSelector = '#drawer-cart';
    this.cartItemsSelector = '#cart-items';
    this.upsellIds = [
      'cart-drawer-upsell-desktop',
      'cart-drawer-upsell-mobile',
    ];
    this.refreshDelay = 100;
  }

  init = () => {
    this.sectionId = document.querySelector(this.drawerSelector)?.dataset.id;
    const cartItems = document.querySelector(this.cartItemsSelector);

    if (!this.sectionId || !cartItems || !this.getUpsellWrappers().length) {
      return;
    }

    this.observer = new MutationObserver(this.onCartItemsChange);
    this.observer.observe(cartItems, { childList: true, subtree: true });
  };

  getUpsellWrappers = () =>
    this.upsellIds.map((id) => document.getElementById(id)).filter(Boolean);

  onCartItemsChange = () => {
    clearTimeout(this.refreshTimeout);
    this.refreshTimeout = setTimeout(this.refresh, this.refreshDelay);
  };

  refresh = async () => {
    try {
      const response = await fetch(
        `${window.location.pathname}?sections=${this.sectionId}`
      );
      const sections = await response.json();
      const newHtml = new DOMParser().parseFromString(
        sections[this.sectionId] ?? '',
        'text/html'
      );

      this.getUpsellWrappers().forEach((wrapper) => {
        const newWrapper = newHtml.getElementById(wrapper.id);

        if (newWrapper) {
          wrapper.innerHTML = newWrapper.innerHTML;
        }
      });
    } catch (error) {
      console.error(error);
    }
  };
}

;// ./src/js/CartItemCount.js
class CartItemCount {
  constructor() {
    this.wrapperSelector = '#cart-items';
    this.sourceSelector = '[data-cart-item-count-source]';
    this.targetSelector = '[data-cart-item-count]';
  }

  init = () => {
    this.wrappers = document.querySelectorAll(this.wrapperSelector);
    if (!this.wrappers.length) {
      return;
    }

    // cart.js replaces #cart-items content after each cart update
    this.observer = new MutationObserver(this.update);
    this.wrappers.forEach((wrapper) => {
      this.observer.observe(wrapper, { childList: true });
    });
  };

  update = () => {
    const source = document.querySelector(
      `${this.wrapperSelector} ${this.sourceSelector}`
    );
    const label = source ? source.textContent.trim() : '';

    document.querySelectorAll(this.targetSelector).forEach((target) => {
      target.textContent = label;
    });
  };
}

;// ./src/js/CartShippingProgress.js
class CartShippingProgress {
  constructor() {
    this.notificationSelector = '[data-free-shipping-notification]';
    this.progressBarSelector = '[data-shipping-progress-bar]';
  }

  init = () => {
    this.update();

    this.observer = new MutationObserver(this.update);
    document
      .querySelectorAll('#cart-shipping-notification')
      .forEach((wrapper) => {
        this.observer.observe(wrapper, { childList: true, subtree: true });
      });
  };

  update = () => {
    document
      .querySelectorAll(this.notificationSelector)
      .forEach((notification) => {
        const progressBar = notification.querySelector(
          this.progressBarSelector
        );
        if (!progressBar) {
          return;
        }

        const percent = this.getProgressPercent(notification);
        progressBar.style.width = `${percent}%`;
        progressBar.setAttribute('aria-valuenow', `${percent}`);
      });
  };

  getProgressPercent = (notification) => {
    const cartTotal = parseInt(notification.dataset.cartTotal, 10) || 0;
    const threshold = parseInt(notification.dataset.threshold, 10) || 0;
    const shopCurrency = notification.dataset.shopCurrency;
    const targetCurrency = notification.dataset.targetCurrency;

    let convertedTotal = cartTotal;
    if (shopCurrency !== targetCurrency && window.Shopify?.currency?.rate) {
      convertedTotal = Math.round(
        cartTotal / (parseFloat(window.Shopify.currency.rate) || 1)
      );
    }

    if (threshold <= 0) {
      return convertedTotal > 0 ? 100 : 0;
    }

    return Math.min(
      100,
      Math.max(0, Math.round((convertedTotal / threshold) * 100))
    );
  };
}

;// ./src/js/Axeptio.js
class Axeptio {
  constructor() {
    this.footerLinksSelector = '.footer .footer__legal-menu-item a';
  }

  init = () => {
    const footerLinks = document.querySelectorAll(this.footerLinksSelector);

    if (footerLinks.length === 0) {
      return;
    }

    footerLinks.forEach((link) => {
      if (link.href.includes('#replaceAxeptio')) {
        // eslint-disable-next-line no-script-url
        link.href = 'javascript:openAxeptioCookies()';
      }
    });
  };
}

;// ./src/js/DeliveryDelay.js
class DeliveryDelay {
  constructor() {
    this.textIconSelector = '.wq-delivery-infos__text-icon';
    this.firstDateSelector = '.wq-delivery-infos__text-bold-date--first';
    this.secondDateSelector = '.wq-delivery-infos__text-bold-date--second';
    this.holidays = [];
  }

  init = async () => {
    const textIcons = document.querySelectorAll(this.textIconSelector);

    if (!textIcons.length > 0) {
      return;
    }

    const today = new Date();
    const year = today.getFullYear();

    try {
      const response = await fetch(
        `https://calendrier.api.gouv.fr/jours-feries/metropole/${year}.json`
      );
      const data = await response.json();
      this.holidays = Object.keys(data).map((date) => new Date(date));
    } catch (error) {
      /* eslint-disable-next-line no-console */
      console.error('Erreur lors du chargement des jours fériés :', error);
    }

    textIcons.forEach((textIcon) => {
      const firstDate = textIcon.querySelector(this.firstDateSelector);
      const secondDate = textIcon.querySelector(this.secondDateSelector);
      const delayMin = firstDate.dataset.deliveryDelayMin;
      const delayMax = secondDate.dataset.deliveryDelayMax;

      if (!firstDate || !secondDate) {
        return;
      }

      const options = {
        month: 'short',
        day: 'numeric',
      };

      const estimatedDate = this.addWorkingDays(today, delayMin);
      firstDate.innerText = estimatedDate.toLocaleDateString('fr-FR', options);

      const estimatedDateMax = this.addWorkingDays(today, delayMax);
      secondDate.innerText = estimatedDateMax.toLocaleDateString(
        'fr-FR',
        options
      );

      textIcon.style.opacity = 1;
    });
  };

  isWorkingDay = (date) => {
    const day = date.getDay();
    const isWeekend = day === 0 || day === 6; // Sunday = 0, Saturday = 6
    const isHoliday = this.holidays.some(
      (holiday) => holiday.toDateString() === date.toDateString()
    );
    return !isWeekend && !isHoliday;
  };

  addWorkingDays = (startDate, daysToAdd) => {
    const date = new Date(startDate);
    let addedDays = 0;
    while (addedDays < daysToAdd) {
      date.setDate(date.getDate() + 1);
      if (this.isWorkingDay(date)) {
        addedDays++;
      }
    }
    return date;
  };
}

;// ./src/js/DiscountTiers.js
class DiscountTiers {
  constructor() {
    this.selectors = {
      root: '[data-wq-discount-tiers]',
      idInput: 'input[name="id"]',
      quantity: '[name=quantity]',
      radios: '[data-wq-tier-radio]',
      qtyInput: '[data-wq-tier-qty-input]',
      variantsData: '[data-wq-tiers-variants]',
      tierCard: '[data-wq-tier-card]',
      tierFull: '[data-wq-tier-full]',
      tierTotal: '[data-wq-tier-total]',
    };
  }

  init() {
    this.root = document.querySelector(this.selectors.root);
    if (!this.root) {
      return;
    }

    this.formId = this.root.dataset.formId;
    this.form = document.getElementById(this.formId);
    if (!this.form) {
      return;
    }

    this.idInput = this.form.querySelector(this.selectors.idInput);
    if (!this.idInput) {
      return;
    }

    this.quantityInput = this.ensureQuantityInput();
    this.radios = Array.from(this.root.querySelectorAll(this.selectors.radios));
    this.qtyInput = this.root.querySelector(this.selectors.qtyInput);

    this.variantPrices = this.readVariantPrices();
    this.variantId = this.idInput.value;

    this.bindEvents();
    this.preselectFromUrl();
    this.apply();
  }

  ensureQuantityInput() {
    const existing =
      document.querySelector(
        `${this.selectors.quantity}[form=${this.formId}]`
      ) || this.form.querySelector(this.selectors.quantity);

    if (existing) {
      return existing;
    }

    const input = document.createElement('input');
    input.type = 'hidden';
    input.name = 'quantity';
    input.value = '1';
    this.form.appendChild(input);

    return input;
  }

  readVariantPrices() {
    const node = this.root.querySelector(this.selectors.variantsData);
    if (!node) {
      return {};
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('[DiscountTiers] invalid variant price map', error);
      return {};
    }
  }

  bindEvents() {
    this.radios.forEach((radio) => {
      radio.addEventListener('change', () => this.apply());
    });

    if (this.qtyInput) {
      this.qtyInput.addEventListener('change', () => {
        this.selectUnitTier();
        this.apply();
      });
    }

    this.idInput.addEventListener('change', () => {
      const value = this.idInput.value;
      if (this.variantPrices[value] !== undefined && value !== this.variantId) {
        this.variantId = value;
        this.apply();
      }
    });

    document.addEventListener('ajaxProduct:added', () => this.apply());
  }

  preselectFromUrl() {
    const lot = new URLSearchParams(window.location.search).get('lot');
    if (!lot) {
      return;
    }

    const radio = this.radios.find((item) => item.value === `lot${lot}`);
    if (radio) {
      radio.checked = true;
    }
  }

  selectUnitTier() {
    const radio = this.radios.find((item) => item.dataset.tierMode === 'unit');
    if (radio) {
      radio.checked = true;
    }
  }

  getSelectedRadio() {
    return this.radios.find((radio) => radio.checked) || this.radios[0];
  }

  getTierQuantity(radio) {
    return parseInt(radio.dataset.tierQuantity, 10) || 1;
  }

  getUnitQuantity() {
    if (!this.qtyInput) {
      return 1;
    }

    const max = parseInt(this.qtyInput.max, 10) || 1;
    const value = parseInt(this.qtyInput.value, 10) || 1;
    const clamped = Math.min(Math.max(value, 1), max);

    if (String(clamped) !== this.qtyInput.value) {
      this.qtyInput.value = clamped;
    }

    return clamped;
  }

  getUnitPrice() {
    return this.variantPrices[this.variantId] || 0;
  }

  getTargetVariantId(radio) {
    return radio.dataset.tierMode === 'box'
      ? radio.dataset.tierVariantId
      : this.variantId;
  }

  getCartQuantity(radio) {
    const mode = radio.dataset.tierMode;

    if (mode === 'box') {
      return 1;
    }
    if (mode === 'units') {
      return this.getTierQuantity(radio);
    }

    return this.getUnitQuantity();
  }

  getDisplayQuantity(radio) {
    return radio.dataset.tierMode === 'unit'
      ? this.getUnitQuantity()
      : this.getTierQuantity(radio);
  }

  apply() {
    const radio = this.getSelectedRadio();
    if (!radio) {
      return;
    }

    this.setFormTarget(
      this.getTargetVariantId(radio),
      this.getCartQuantity(radio)
    );
    this.refreshCards();
  }

  setFormTarget(variantId, quantity) {
    if (!variantId) {
      return;
    }

    this.idInput.disabled = false;
    this.idInput.value = variantId;
    this.quantityInput.value = quantity;
  }

  refreshCards() {
    const unitPrice = this.getUnitPrice();

    this.radios.forEach((radio) => {
      const card = radio.closest(this.selectors.tierCard);
      if (!card) {
        return;
      }

      card.classList.toggle('is-selected', radio.checked);

      const discount = parseInt(radio.dataset.tierDiscount, 10) || 0;
      const fullPrice = unitPrice * this.getDisplayQuantity(radio);
      const finalPrice = Math.round((fullPrice * (100 - discount)) / 100);

      const fullNode = card.querySelector(this.selectors.tierFull);
      if (fullNode) {
        fullNode.textContent = this.formatMoney(fullPrice);
      }

      const totalNode = card.querySelector(this.selectors.tierTotal);
      if (totalNode) {
        totalNode.textContent = this.formatMoney(finalPrice);
      }
    });
  }

  formatMoney(cents) {
    if (
      window.Woolman &&
      window.Woolman.Utils &&
      window.Woolman.Utils.formatMoney
    ) {
      return window.Woolman.Utils.formatMoney(cents);
    }

    return (cents / 100).toFixed(2);
  }
}

;// ./src/js/SectionHeaderTextReadMore.js
class SectionHeaderTextReadMore {
  constructor() {
    this.containerSelector = '.section-header__text';
    this.textSelector = '.section-header__text-content';
    this.toggleSelector = '.section-header__text-toggle';
  }

  init = () => {
    const container = document.querySelector(this.containerSelector);
    if (!container) {
      return;
    }

    const text = container.querySelector(this.textSelector);
    const toggle = container.querySelector(this.toggleSelector);
    if (!text || !toggle) {
      return;
    }

    const isTruncated = text.scrollHeight > text.clientHeight + 1;
    if (!isTruncated) {
      return;
    }

    text.classList.add('section-header__text-content--truncated');
    toggle.hidden = false;
    toggle.addEventListener('click', () =>
      this.toggleDescription(text, toggle)
    );
  };

  toggleDescription = (text, toggle) => {
    const expanded = text.classList.toggle(
      'section-header__text-content--expanded'
    );
    text.classList.toggle('section-header__text-content--truncated', !expanded);
    toggle.setAttribute('aria-expanded', expanded);
    toggle.textContent = expanded
      ? toggle.dataset.labelLess
      : toggle.dataset.labelMore;
  };
}

;// ./src/main.js




// JS goes here





window.addEventListener('load', () => {
  const classes = [
    new CartShippingProgress(),
    new CartItemCount(),
    new SectionHeaderTextReadMore(),
    new DeliveryDelay(),
    new CartDrawerUpsell(),
    new DiscountTiers(),
    new Axeptio(),
  ];
  classes.forEach((classe) => classe.init());
});

/******/ })()
;